import dynamic from "next/dynamic";
import Theme from "@/components/Theme";
import Hero from "@/organisms/Hero";
import useIntersectionObserver from "@/utils/useIntersectionObserver";

// Dynamic imports with ssr: false
const DynamicTrustpilot = dynamic(() => import("@/organisms/Trustpilot"), {
  ssr: false,
});
const DynamicAudioLayout = dynamic(() => import("@/organisms/AudioLayout"), {
  ssr: false,
});
const DynamicSlider = dynamic(() => import("@/components/organisms/Slider"), {
  ssr: false,
});
const DynamicServicesSectionTwoCol = dynamic(
  () => import("@/components/organisms/ServicesSectionTwoCol"),
  {
    ssr: false,
  }
);
const DynamicRegistrationForm = dynamic(
  () => import("@/components/molecules/RegistrationForm"),
  {
    ssr: false,
  }
);
const DynamicConnectIntegration = dynamic(
  () => import("@/components/organisms/ConnectIntegration"),
  {
    ssr: false,
  }
);

const DynamicBadgeSection = dynamic(
  () => import("@/components/organisms/BadgeSection"),
  {
    ssr: false,
  }
);

const DynamicMembershipPricingSection = dynamic(
  () => import("@/components/organisms/MembershipPricingSection"),
  {
    ssr: false,
  }
);

// Static data imports
import * as hvacAnsweringService from "@/data/lp/hvac-answering-service";
import * as homeServicesAnsweringService from "@/data/lp/home-services-answering-service";
import * as constructionAnsweringService from "@/data/lp/contractors-answering-service";
import * as virtualReceptionistService from "@/data/lp/virtual-receptionist-service";
import * as virtualReceptionistServiceV2 from "@/data/lp/virtual-receptionist-service-v2";
import * as afterHoursAnsweringService from "@/data/lp/after-hours-answering-service";
import * as afterHoursAnsweringServiceV2 from "@/data/lp/after-hours-answering-service-v2";
import * as smallBusinessAnsweringService from "@/data/lp/small-business-answering-service";
import * as plumbingAnsweringService from "@/data/lp/plumbing-answering-service-v2";
import * as telephoneAnsweringServiceV2 from "@/data/lp/telephone-answering-service-v2";
import * as riaAffinityProgram from "@/data/lp/ria-affinity-program";

export async function getServerSideProps({ params: { landingPages } }) {
  let data = {};
  const contentMap = {
    "home-services-answering-service": homeServicesAnsweringService,
    "contractors-answering-service": constructionAnsweringService,
    "virtual-receptionist-service": virtualReceptionistService,
    "virtual-receptionist-services": virtualReceptionistServiceV2,
    "after-hours-answering-service": afterHoursAnsweringService,
    "after-hours-answering-services": afterHoursAnsweringServiceV2,
    "hvac-answering-service": hvacAnsweringService,
    "small-business-answering-service": smallBusinessAnsweringService,
    "plumbing-answering-service": plumbingAnsweringService,
    "telephone-answering-service": telephoneAnsweringServiceV2,
    "ria-affinity-program": riaAffinityProgram,
  };

  if (!landingPages || !contentMap[landingPages]) {
    return {
      notFound: true,
    };
  }

  data.marketing = { ...contentMap[landingPages]["marketing"] };
  data.content = { ...contentMap[landingPages]["content"] };

  return {
    props: {
      data,
      pagename: landingPages,
    },
  };
}

export default function LandingPages({ data, pagename }) {
  const seoData = data?.marketing;
  const pagedata = data?.content;

  const ppcSecondaryVariant = [
    "virtual-receptionist-services",
    "after-hours-answering-services",
    "plumbing-answering-service",
    "telephone-answering-service",
    "small-business-answering-service",
    "hvac-answering-service",
    "ria-affinity-program",
  ];

  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
  });

  const hero = pagedata?.hasOwnProperty("hero") && (
    <Hero
      heroTitle={pagedata.hero.title}
      heroContent={pagedata.hero.content}
      hasTrustpilot={pagedata.hero.hasTrustpilot}
      variant={pagedata.hero.variant}
      regFormOptions={pagedata.hero.regFormOptions}
    />
  );

  return (
    <Theme
      mainClassName="main"
      hero={hero}
      seo={seoData}
      headerVariant="mini"
      hasHeaderCta
      isWhiteHeader
      isFooterMini
      hasTopAudioBannerLP={pagedata?.hasTopAudioBannerLP}
      refMain={elementRef}
    >
      {ppcSecondaryVariant.includes(pagename) &&
        pagedata?.hasOwnProperty("audioLayout") && (
          <DynamicAudioLayout
            title={pagedata.audioLayout.title}
            image={pagedata.audioLayout.image}
            isLPvariant
            className={pagedata.audioLayout.className}
          />
        )}
      {pagedata?.hasOwnProperty("servicesSectionTwoCol") && (
        <DynamicServicesSectionTwoCol
          bgColor={pagedata.servicesSectionTwoCol.bgColor}
          label={pagedata.servicesSectionTwoCol.label}
          title={pagedata.servicesSectionTwoCol.title}
          subText={pagedata.servicesSectionTwoCol.subText}
          listData={pagedata.servicesSectionTwoCol.listData}
          image={pagedata.servicesSectionTwoCol.image}
          contentData={pagedata.servicesSectionTwoCol.contentData}
          hasTopAudioBannerLP={
            pagedata.servicesSectionTwoCol.hasTopAudioBannerLP
          }
        />
      )}
      {pagename === "ria-affinity-program" &&
        pagedata?.hasOwnProperty("badgeSection") && (
          <DynamicBadgeSection
            label={pagedata.badgeSection.label}
            title={pagedata.badgeSection.title}
            bgColor={pagedata.badgeSection.bgColor}
            image={pagedata.badgeSection.image}
            hasAestheticBg={pagedata.badgeSection.hasAestheticBg}
          />
        )}

      {pagename === "ria-affinity-program" &&
        pagedata?.hasOwnProperty("membershipPricingSection") && (
          <DynamicMembershipPricingSection
            title={pagedata.membershipPricingSection.title}
            content={pagedata.membershipPricingSection.content}
            bgColor={pagedata.membershipPricingSection.bgColor}
            customerInfo={pagedata.membershipPricingSection.customerInfo}
            contactInfo={pagedata.membershipPricingSection.contactInfo}
            termsData={pagedata.membershipPricingSection.termsData}
            plansCardData={pagedata.membershipPricingSection.plansCardData}
          />
        )}

      {pagedata?.hasOwnProperty("trustPilot") && (
        <DynamicTrustpilot
          title={pagedata.trustPilot.title}
          variant={pagedata.trustPilot.variant}
        />
      )}
      {isIntersecting &&
        !ppcSecondaryVariant.includes(pagename) &&
        pagedata?.hasOwnProperty("audioLayout") && (
          <DynamicAudioLayout
            title={pagedata.audioLayout.title}
            image={pagedata.audioLayout.image}
          />
        )}
      {isIntersecting && pagedata?.hasOwnProperty("slider") && (
        <DynamicSlider
          data={pagedata.slider.data}
          variant={pagedata.slider.variant}
          title={pagedata.slider.title}
          subTitle={pagedata.slider.subTitle}
        />
      )}
      {isIntersecting &&
        pagedata?.hasOwnProperty("tileLessConnectIntegration") && (
          <DynamicConnectIntegration
            title={pagedata.tileLessConnectIntegration.title}
            hasTitleDot={pagedata.tileLessConnectIntegration.hasTitleDot}
            tileData={pagedata.tileLessConnectIntegration.tileData}
            bgColor={pagedata.tileLessConnectIntegration.bgColor}
          />
        )}
      {isIntersecting && pagedata?.hasOwnProperty("miniRegister") && (
        <DynamicRegistrationForm
          title={pagedata.miniRegister.title}
          content={pagedata.miniRegister.content}
          hasBusinessNameField={false}
          variant="mini"
          formUniqueName={`page-${pagename}-mini`}
        />
      )}
    </Theme>
  );
}
